/**
 * 组件类型常量管理
 * 统一管理组件类型相关的配置，避免在多个文件中重复定义
 * 任务类型、任务模式、任务周期、榜单类型、榜单周期等完全依赖API数据
 */

// 组件类型定义（本地固定配置）
export const UNIT_TYPES = [
  { value: 1, label: '货币组件', tagType: 'success', color: '#67c23a' },
  { value: 2, label: '奖池组件', tagType: 'primary', color: '#409eff' },
  { value: 3, label: '任务组件', tagType: 'warning', color: '#e6a23c' },
  { value: 4, label: '排行组件', tagType: 'danger', color: '#f56c6c' },
  { value: 5, label: '步频奖池', tagType: 'info', color: '#909399' },
  { value: 6, label: '商店组件', tagType: 'success', color: '#67c23a' },
  { value: 7, label: '货币合成组件', tagType: 'primary', color: '#189423' },
  { value: 8, label: '队伍组件', tagType: 'warning', color: '#e6a23c' },
  { value: 9, label: '分享奖励组件', tagType: 'primary', color: '#409eff' },
  { value: 10, label: '刮刮乐组件', tagType: 'info', color: '#909399' }
]

// 货币类型定义（本地固定配置）
export const CURRENCY_TYPES = [
  { value: 1, label: '金币' },
  { value: 9, label: '钻石' },
  { value: 9999, label: '活动货币' }
]

// 工具函数：根据值获取组件类型信息
export function getUnitTypeInfo(value) {
  return UNIT_TYPES.find(type => type.value === value) || { 
    value, 
    label: '未知类型', 
    tagType: 'info', 
    color: '#909399' 
  }
}

// 工具函数：根据值获取组件类型名称
export function getUnitTypeName(value) {
  const typeInfo = getUnitTypeInfo(value)
  return typeInfo.label
}

// 工具函数：根据值获取组件类型标签样式
export function getUnitTypeTagType(value) {
  const typeInfo = getUnitTypeInfo(value)
  return typeInfo.tagType
}

// 工具函数：根据值获取组件类型颜色
export function getUnitTypeColor(value) {
  const typeInfo = getUnitTypeInfo(value)
  return typeInfo.color
}

// 工具函数：根据值获取货币类型名称
export function getCurrencyTypeName(value) {
  const currencyType = CURRENCY_TYPES.find(type => type.value === value)
  return currencyType ? currencyType.label : `货币类型(${value})`
}

// 工具函数：根据值获取任务类型名称（完全依赖API数据）
export function getTaskTypeName(value, taskConstants = []) {
  // 处理嵌套数据结构：taskConstants.taskTypes 或直接数组
  const constants = taskConstants.taskTypes || taskConstants
  const taskType = constants.find(item => item.value === value)
  return taskType ? taskType.name : `任务类型(${value})`
}

// 工具函数：根据值获取任务模式名称（完全依赖API数据）
export function getTaskModeName(value, taskModeConstants = []) {
  // 处理嵌套数据结构：taskConstants.taskModes 或直接数组
  const constants = taskModeConstants.taskModes || taskModeConstants
  const taskMode = constants.find(item => item.value === value)
  return taskMode ? taskMode.name : `任务模式(${value})`
}

// 工具函数：根据值获取任务周期名称（完全依赖API数据）
export function getTaskDurationName(value, taskDurationConstants = []) {
  // 处理嵌套数据结构：taskConstants.taskDurations 或直接数组
  const constants = taskDurationConstants.taskDurations || taskDurationConstants
  const taskDuration = constants.find(item => item.value === value)
  return taskDuration ? taskDuration.name : `任务周期(${value})`
}

// 工具函数：根据值获取榜单类型名称（完全依赖API数据）
export function getRankTypeName(value, rankTypeConstants = []) {
  const rankType = rankTypeConstants.find(item => item.value === value)
  return rankType ? rankType.name : `榜单类型(${value})`
}

// 工具函数：根据值获取榜单周期类型名称（完全依赖API数据）
export function getRankDurationName(value, rankDurationConstants = []) {
  const rankDuration = rankDurationConstants.find(item => item.value === value)
  return rankDuration ? rankDuration.name : `榜单周期(${value})`
}

// 默认组件配置模板
export function getDefaultUnitConfigs() {
  return {
    currencyUnit: {
      icon: '',
      type: 9999,
      showRate: 1,
      enableAutoDraw: false,
      awardPoolId: 0,
      enableGive: false,
      giveConfig: {
        onceMax: 0
      }
    },
    awardPoolUnit: {
      costUnitIds: [],
      timesRate: 1,
      awardsKey: '',
      enablePayPool: false,
      minPayUsd: 0
    },
    taskUnit: {
      type: null,
      mode: null,
      duration: null,
      checkParamsSet: [],
      autoGiveAward: true,
      teamTask: false,
      teamUnitId: 0,
      config: {
        checkParams: 0,
        showRate: 1,
        checkText: '',
        limit: -1,
        awardsKey: '',
        autoSendNotice: false,
        notice: ''
      },
      configs: []
    },
    rankUnit: {
      rankType: 0,
      startTime: 0,
      endTime: 0,
      showTop: 10,
      awardTop: 10,
      scoreRate: 1,
      scoreIcon: '',
      checkParamsSet: [],
      autoGiveAward: false,
      autoSendGlobalNotice: false,
      rewardConfigs: [],
      noticeSupportChannel: [],
      enableRankLabel: false,
      rankLabelConfigs: [],
      rankDuration: 0,
      enablePool: false,
      teamRank: false,
      teamUnitId: 0,
      rankPoolInfo: {
        poolRate: 0,
        basePoolAmount: '',
        maxPoolAmount: '',
        showRate: 0,
        rankPoolConfigs: []
      },
      enableSonRank: false,
      sonRankConfig: {
        showTop: 1,
        awardTop: 1,
        diffSex: false
      }
    },
    teamUnit: {
      maxMember: 0,
      duration: 0,
      memberNotice: '',
      captainNotice: ''
    },
    stepPoolUnit: {
      maxSteps: 0,
      costUnitIds: [],
      refreshCost: 0,
      sonPools: [],
      stepConfigs: [],
      stepRewards: []
    },
    shopUnit: {
      costUnitIds: [],
      items: []
    },
    currencyMergeUnit: {
      costCurrencyUnitIds: [],
      awardsKey: ''
    },
    shareUnit: {
      costUnitIds: [],
      needCost: 0,
      shareAwardsKey: '',
      shareToAwardsKey: '',
      shareToNotice: ''
    },
    scratchCardUnit: {
      poolUnitId: 0,
      mappingList: [],
      lowPoolUnitId: 0,
      randomMin: 0,
      randomMax: 0
    }
  }
}

// 获取组件类型对应的配置键名
export function getUnitConfigKey(unitType) {
  const keyMap = {
    1: 'currencyUnit',
    2: 'awardPoolUnit', 
    3: 'taskUnit',
    4: 'rankUnit',
    5: 'stepPoolUnit',
    6: 'shopUnit',
    7: 'currencyMergeUnit',
    8: 'teamUnit',
    9: 'shareUnit',
    10: 'scratchCardUnit'
  }
  return keyMap[unitType]
}

// 导出所有常量
export default {
  UNIT_TYPES,
  CURRENCY_TYPES,
  getUnitTypeInfo,
  getUnitTypeName,
  getUnitTypeTagType,
  getUnitTypeColor,
  getCurrencyTypeName,
  getTaskTypeName,
  getTaskModeName,
  getTaskDurationName,
  getRankTypeName,
  getRankDurationName,
  getDefaultUnitConfigs,
  getUnitConfigKey
}